import styles from './index.module.sass';
import { ReactNode } from 'react';
import { IRecipeComponent } from '@/widgets/recipe/types';
import { CompAdditional } from './components/mixes-tooltip/index.component';
import { ILeftoverTooltipData, LeftoversTooltip } from './components/leftover-tooltip';

import bgMix from '@@/mixing/components/recipes-detail/icons/bg-2.svg';
import dropIcon from '@@/mixing/components/recipes-detail/icons/drop.svg';
import dropGreen from '@@/mixing/components/recipes-detail/icons/drop-green.svg';
import strucIcon from '@@/mixing/components/recipes-detail/icons/struc.svg';
import tempIcon from '@@/mixing/components/recipes-detail/icons/temp.svg';
import rotateArrowIcon from '@@/mixing/components/recipes-detail/icons/rotate-arrow.svg';
import { ButtonMain } from '../button';
import { data } from 'yandex-maps';
import { Humidity } from '@/shared/ui/icons';
import { humidityLvlColor } from '../../utils/get-humidity-color';
import classNames from 'classnames';
import { Tooltip } from 'antd';
import { DetalisationTooltip } from '../detalisation-tooltip';
import { useNavigate } from 'react-router-dom';
import { cancelTradeOffer } from '@/modules/mixing/helpers/dalete-offer.helper';
import { $selectedRecipeStore } from '@/modules/mixing/store/recipes.store';
import { useStore } from 'effector-react';
import {
  $topRecipesForWarehouse,
  fetchTopRecipesForWarehouse,
  fetchWarehousesForMixingDataFx,
} from '@/widgets/recipe/model';
import { spreadWheatType } from '../../helpers/wheat-type.helper';
import { getBestMarketPricesFx } from '@/modules/mixing/store/analytical-mixing.store';
import { RubleRegular } from '../ruble-regular';
import { useWindowDimensions } from '../../hooks/use-window-dimensions.hook';

export const convertPriceToString = (price: number) => {
  return price ? `${price.toLocaleString('ru')} ₽` : '0 ₽';
};

interface IArgs {
  idx: number | string;
  weight: number;
  wheatClass?: number;
  type?: string;
  price: number;
  components?: IRecipeComponent[];
  markered?: boolean;
  nettPrice?: number;
  profit: number;
  onClick: () => void;
  mode: 'leftover' | 'mix';
  distance?: number;
  pricePerTon?: number;
  offerHumidity?: number;
  buyer?: string;
  logitPrice?: number;
  sort?: string;
  status?: string;
  leftoverTooltip?: ILeftoverTooltipData;
  whs?: ReactNode;
  params?: { value: string; units: string; name: string }[];
  hasTrade?: number;
  tradeClass?: string;
}

function SpecItem({
  style,
  label,
  value,
  postfix,
}: {
  label: ReactNode;
  value: ReactNode;
  postfix?: ReactNode;
  style?: React.CSSProperties;
}) {
  return (
    <div className={styles.specItem} style={style}>
      <span className={styles.label}>{label}</span>
      <span className={styles.value}>
        {value} <span className={styles.postfix}>{postfix}</span>
      </span>
    </div>
  );
}

export const MixItem = ({
  idx,
  weight,
  type,
  sort,
  price,
  components,
  nettPrice,
  profit,
  onClick,
  mode,
  distance,
  pricePerTon,
  buyer,
  logitPrice,
  leftoverTooltip,
  whs,
  params,
  markered,
  offerHumidity,
  status,
  hasTrade,
  tradeClass,
}: IArgs) => {
  const navigate = useNavigate();
  const humidity = offerHumidity;
  const activeWh = useStore($selectedRecipeStore);
  const isStationary = activeWh?.type === '1';
  const topData = useStore($topRecipesForWarehouse);
  const { width } = useWindowDimensions();

  return (
    <div
      className={classNames(
        styles.root,
        markered && styles.markeredMix,
        isStationary && styles.heap,
      )}
    >
      <div className={styles.mainInfo}>
        <div className={styles.row}>
          <span
            className={styles.value}
            style={{ display: 'flex', gap: '8px', alignItems: 'center' }}
          >
            Микс №{+idx}{' '}
            {components && (
              <CompAdditional
                idx={1}
                buyer={[components[0]?.buyer_company, components[1]?.buyer_company]}
                qty={[components[0]?.sleeves_count, components[1]?.sleeves_count]}
                price={[components[0]?.price_all, components[1]?.price_all]}
                pricePerTon={[
                  components[0]?.price_per_ton,
                  components[1]?.price_per_ton,
                ]}
                distance={[components[0]?.distance, components[1]?.distance]}
                netto={[components[0]?.price_netto, components[1]?.price_netto]}
                weight={[components[0]?.weight, components[1]?.weight]}
                priceLogistic={[
                  components[0]?.price_logistic,
                  components[1]?.price_logistic,
                ]}
              />
            )}
          </span>
        </div>
        <div className={styles.row}>
          <span className={styles.label}>вес микса</span>
          <span className={styles.value}>
            {weight && Number(weight).toLocaleString('RU-ru')} т
          </span>
        </div>
      </div>
      <div
        className={
          isStationary && width > 1850 ? styles.wheatHeapInfo : styles.wheatInfo
        }
      >
        {isStationary && width > 1850 && (
          <svg
            width="304"
            height="140"
            viewBox="0 0 304 140"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M1.40459 126.736L1.40466 126.736C28.6432 71.2088 70.6129 28.2785 112.779 9.02848C137.696 -2.34285 166.312 -2.34281 191.221 9.02848C233.387 28.2785 275.349 71.2088 302.595 126.736L303.044 126.516L302.595 126.736C305.431 132.513 301.254 139.249 294.811 139.249H152.004H9.18919C2.74558 139.249 -1.4314 132.513 1.40459 126.736Z"
              fill={'#F5EBD3'}
              stroke={'#F5EBD3'}
            />
          </svg>
        )}
        <div
          className={classNames(
            isStationary && width > 1850 && styles.infoText,
            styles.infoE,
          )}
        >
          <span className={styles.classInfo}>
            Пшеница,{' '}
            {hasTrade ? (
              tradeClass
            ) : Number(params?.find((item) => item.name === 'Класс')?.value) === 0 ? (
              <>
                н/д <DetalisationTooltip status="ZeroClass" />
              </>
            ) : (
              `${params?.find((item) => item.name === 'Класс')?.value}-й класс `
            )}
          </span>
          <span className={styles.typeInfo}>
            {hasTrade
              ? `${spreadWheatType(Number(type))?.sort} ${
                  spreadWheatType(Number(type))?.type
                }`
              : `${sort} ${type}`}
          </span>
          {params && params.length > 0 && (
            <div className={styles.params}>
              <span className={styles.param}>
                <Humidity
                  fill={humidityLvlColor(
                    Number(
                      params?.find((item) => item.name === 'Влажность культуры')?.value,
                    ),
                  )}
                />{' '}
                {Number(
                  params?.find((item) => item.name === 'Влажность культуры')?.value,
                ).toFixed(2)}{' '}
                %
              </span>
              <span className={styles.param}>
                <img src={strucIcon} className={styles.icon} />{' '}
                {Math.floor(
                  Number(params?.find((item) => item.name === 'Клейковина')?.value),
                )}{' '}
                %
              </span>
              <span className={styles.param}>
                <img src={tempIcon} className={styles.icon} />{' '}
                {Math.floor(
                  Number(params?.find((item) => item.name === 'Температура')?.value),
                )}{' '}
                °C
              </span>
            </div>
          )}
        </div>
      </div>
      <>
        <div className={styles.buyerInfo}>
          <div className={styles.topInfo}>
            <span className={styles.buyerLabel}>
              <Tooltip title={buyer}>{buyer}</Tooltip>
            </span>
            <span className={styles.action} style={{ marginLeft: '4px' }}>
              заменить покупателя <img src={rotateArrowIcon} className={styles.icon} />
            </span>
          </div>
          <div className={styles.bottomArea}>
            <div className={styles.col}>
              <span className={styles.label}>цена</span>
              <span className={styles.value}>
                {(
                  <>
                    {Number(pricePerTon)?.toLocaleString('ru-RU')}{' '}
                    <RubleRegular customSymbol="₽/т" p={0} />
                  </>
                ) || 'н/д'}
              </span>
            </div>
            <div className={styles.col}>
              <span className={styles.label}>
                логистика{' '}
                {!logitPrice && !distance ? (
                  <DetalisationTooltip
                    status={status as any}
                    rangeKm={distance}
                    navigate={navigate}
                  />
                ) : !logitPrice && distance ? (
                  <DetalisationTooltip
                    status={status as any}
                    rangeKm={distance}
                    red={true}
                    navigate={navigate}
                  />
                ) : (
                  ''
                )}
              </span>
              <span className={styles.value}>
                {!logitPrice && !distance ? (
                  <span className={styles.selfDelivery}>
                    {status === 'BuyerPickupEnforced'
                      ? 'самовывоз'
                      : 'по договоренности'}
                  </span>
                ) : (
                  <>
                    {Number(logitPrice)?.toLocaleString('ru') || '0'}{' '}
                    <RubleRegular p={0} />{' '}
                    <span className={styles.innerRegular}>
                      {distance?.toLocaleString('ru') || '0'} км
                    </span>
                  </>
                )}
              </span>
            </div>
            {humidity && (
              <div className={styles.col}>
                <span className={styles.label}>влажность</span>
                <span className={classNames(styles.value, styles.humidity)}>
                  <Humidity fill={humidityLvlColor(humidity)} /> {humidity.toFixed(2)}%
                </span>
              </div>
            )}
          </div>
        </div>
        <div className={styles.businessInfo}>
          <div className={styles.row}>
            <span className={styles.label}>стоимость микса</span>
            <span className={styles.value}>
              {price && Number(price).toLocaleString('RU-ru')}{' '}
              <span className={styles.innerRegular}>₽</span>
            </span>
          </div>
          <div className={styles.row}>
            <span className={styles.label}>
              стоимость с уч. логистики <span className={styles.innerRegular}></span>
            </span>
            <span className={styles.value}>
              {nettPrice && Number(nettPrice).toLocaleString('RU-ru')}{' '}
              <span className={styles.innerRegular}>₽</span>
            </span>
          </div>
        </div>
      </>

      {!hasTrade ? (
        <div className={styles.actionArea}>
          <span className={styles.profitLabel}>выгода от миксования</span>
          <span className={styles.profitValue}>
            + {profit.toLocaleString('RU-ru', { maximumFractionDigits: 0 })}
            <RubleRegular fsize=".9em" p={2} />
          </span>
          <ButtonMain
            onClick={onClick}
            className={styles.mixAction}
            label={'показать рецепт'}
          />
        </div>
      ) : (
        <div className={styles.busyAction}>
          <span className={styles.busyLabel}>микс участвует в сделке</span>
          <span
            className={styles.busyButton}
            onClick={() =>
              cancelTradeOffer(
                Number(hasTrade),
                async () => {
                  activeWh?.id && (await fetchTopRecipesForWarehouse(activeWh?.id));
                  await fetchWarehousesForMixingDataFx({});
                  await getBestMarketPricesFx(true);
                },
                isStationary,
                topData?.mix_signature,
              )
            }
          >
            отменить сделку
          </span>
        </div>
      )}
      <img className={styles.bgImg} src={bgMix} />
    </div>
  );
};
